import type { Locale } from '@/config/locales';
import { getPartnerVisibilityStrings } from './partnerVisibility';

interface MarketingCopy {
  navigationTitle: string;
  continueLogin: string;
  pageHeading: string;
  sectionTitle: string;
  sectionDescription: string;
  storeSectionTitle: string;
  statusNotChosen: string;
  statusGranted: string;
  statusOff: string;
  statusWithdrawn: string;
  statusExpired: string;
  statusContactBlocked: string;
  statusUnavailable: string;
  resolveContactBlock: string;
  grant: string;
  decline: string;
  renew: string;
  staleNotice: string;
}

interface MarketingWithdrawalCopy {
  withdrawConfirmTitle: string;
  withdrawConfirmBody: string;
  withdrawConfirmCancel: string;
  withdrawConfirmAction: string;
}

export interface MarketingConsentStrings
  extends MarketingCopy,
    MarketingWithdrawalCopy {
  noticeLink: string;
  noticeVersion: string;
  withdraw: string;
  withdrawPrevious: string;
  loading: string;
  loadError: string;
  retry: string;
  saving: string;
  mutationError: string;
  grantSuccess: string;
  withdrawSuccess: string;
}

const copyByLocale: Record<Locale, MarketingCopy> = {
  es: {
    continueLogin: 'Continuar',
    navigationTitle: 'Configuración de privacidad',
    pageHeading: 'Configuración de privacidad',
    sectionTitle: 'Comunicaciones de marketing',
    sectionDescription:
      'Elige si OLULO puede usar tus datos para enviarte promociones, novedades y comunicaciones de marketing.',
    storeSectionTitle: 'Compartir datos con tiendas',
    statusNotChosen: 'Aún no has elegido una preferencia.',
    statusGranted: 'Consentimiento de marketing vigente',
    statusOff: 'Las comunicaciones de marketing están desactivadas',
    statusWithdrawn: 'No aceptaste o retiraste el consentimiento',
    statusExpired: 'Tu consentimiento anterior ya no está vigente',
    statusContactBlocked:
      'Tu consentimiento quedó guardado, pero el marketing sigue bloqueado para este número',
    statusUnavailable: 'Esta preferencia no se puede cambiar ahora',
    resolveContactBlock: 'Intentar reactivar los mensajes',
    grant: 'Dar consentimiento',
    decline: 'No aceptar',
    renew: 'Aceptar el aviso actualizado',
    staleNotice:
      'El aviso cambió. Revisa la versión actualizada y vuelve a confirmar si deseas aceptar.',
  },
  en: {
    continueLogin: 'Continue',
    navigationTitle: 'Privacy settings',
    pageHeading: 'Privacy settings',
    sectionTitle: 'Marketing communications',
    sectionDescription:
      'Choose whether OLULO may use your data to send promotions, news, and marketing communications.',
    storeSectionTitle: 'Data sharing with Stores',
    statusNotChosen: 'You have not chosen a preference yet.',
    statusGranted: 'Marketing consent current',
    statusOff: 'Marketing communications are off',
    statusWithdrawn: 'Consent declined or withdrawn',
    statusExpired: 'Your previous consent is no longer current',
    statusContactBlocked:
      'Your consent is saved, but marketing remains blocked for this phone number',
    statusUnavailable: 'This preference cannot be changed right now',
    resolveContactBlock: 'Try to restore messages',
    grant: 'Give consent',
    decline: 'Do not consent',
    renew: 'Agree to the updated notice',
    staleNotice:
      'The notice changed. Review the updated version and confirm again if you want to agree.',
  },
  ko: {
    continueLogin: '계속하기',
    navigationTitle: '개인정보 설정',
    pageHeading: '개인정보 설정',
    sectionTitle: '마케팅 정보 수신',
    sectionDescription:
      'OLULO가 프로모션, 소식 및 마케팅 정보를 보내기 위해 개인정보를 이용할 수 있는지 선택하세요.',
    storeSectionTitle: '스토어 개인정보 제공',
    statusNotChosen: '아직 동의 여부를 선택하지 않았습니다.',
    statusGranted: '현재 유효한 마케팅 동의',
    statusOff: '마케팅 정보 수신이 꺼져 있습니다',
    statusWithdrawn: '동의하지 않았거나 철회했습니다',
    statusExpired: '이전 동의가 더 이상 유효하지 않습니다',
    statusContactBlocked:
      '동의는 저장되었지만 이 전화번호의 마케팅 발송은 계속 차단됩니다',
    statusUnavailable: '현재 이 설정을 변경할 수 없습니다',
    resolveContactBlock: '메시지 수신 다시 켜기',
    grant: '동의함',
    decline: '동의하지 않음',
    renew: '최신 내용에 다시 동의',
    staleNotice:
      '동의 내용이 변경되었습니다. 최신 내용을 확인한 뒤 동의하려면 다시 확인해 주세요.',
  },
  pt: {
    continueLogin: 'Continuar',
    navigationTitle: 'Configurações de privacidade',
    pageHeading: 'Configurações de privacidade',
    sectionTitle: 'Comunicações de marketing',
    sectionDescription:
      'Escolha se a OLULO pode usar seus dados para enviar promoções, novidades e comunicações de marketing.',
    storeSectionTitle: 'Compartilhamento de dados com lojas',
    statusNotChosen: 'Você ainda não escolheu uma preferência.',
    statusGranted: 'Consentimento de marketing vigente',
    statusOff: 'As comunicações de marketing estão desativadas',
    statusWithdrawn: 'Consentimento recusado ou retirado',
    statusExpired: 'Seu consentimento anterior não está mais vigente',
    statusContactBlocked:
      'Seu consentimento foi salvo, mas o marketing continua bloqueado para este número',
    statusUnavailable: 'Esta preferência não pode ser alterada agora',
    resolveContactBlock: 'Tentar reativar as mensagens',
    grant: 'Dar consentimento',
    decline: 'Não consentir',
    renew: 'Aceitar o aviso atualizado',
    staleNotice:
      'O aviso mudou. Revise a versão atualizada e confirme novamente se quiser aceitar.',
  },
  fr: {
    continueLogin: 'Continuer',
    navigationTitle: 'Paramètres de confidentialité',
    pageHeading: 'Paramètres de confidentialité',
    sectionTitle: 'Communications marketing',
    sectionDescription:
      'Choisissez si OLULO peut utiliser vos données pour envoyer des promotions, actualités et communications marketing.',
    storeSectionTitle: 'Partage de données avec les établissements',
    statusNotChosen: 'Vous n’avez pas encore choisi de préférence.',
    statusGranted: 'Consentement marketing en vigueur',
    statusOff: 'Les communications marketing sont désactivées',
    statusWithdrawn: 'Consentement refusé ou retiré',
    statusExpired: 'Votre ancien consentement n’est plus valable',
    statusContactBlocked:
      'Votre consentement est enregistré, mais le marketing reste bloqué pour ce numéro',
    statusUnavailable: 'Cette préférence ne peut pas être modifiée actuellement',
    resolveContactBlock: 'Tenter de réactiver les messages',
    grant: 'Donner mon consentement',
    decline: 'Ne pas consentir',
    renew: 'Accepter l’avis mis à jour',
    staleNotice:
      'L’avis a changé. Consultez la version mise à jour et confirmez de nouveau pour accepter.',
  },
  zh: {
    continueLogin: '继续',
    navigationTitle: '隐私设置',
    pageHeading: '隐私设置',
    sectionTitle: '营销信息',
    sectionDescription: '选择是否允许 OLULO 使用您的数据发送优惠、资讯和营销信息。',
    storeSectionTitle: '与门店共享数据',
    statusNotChosen: '您尚未选择偏好。',
    statusGranted: '营销同意当前有效',
    statusOff: '营销信息已关闭',
    statusWithdrawn: '已拒绝或撤回同意',
    statusExpired: '您之前的同意已失效',
    statusContactBlocked: '您的同意已保存，但此电话号码仍禁止接收营销信息',
    statusUnavailable: '目前无法更改此偏好',
    resolveContactBlock: '尝试恢复消息',
    grant: '同意',
    decline: '不同意',
    renew: '同意更新后的声明',
    staleNotice: '声明已更改。请查看更新版本，如要同意请再次确认。',
  },
  ja: {
    continueLogin: '続ける',
    navigationTitle: 'プライバシー設定',
    pageHeading: 'プライバシー設定',
    sectionTitle: 'マーケティング情報',
    sectionDescription:
      'OLULOがプロモーションやニュースなどのマーケティング情報を送るためにデータを利用できるか選択してください。',
    storeSectionTitle: '店舗とのデータ共有',
    statusNotChosen: 'まだ設定を選択していません。',
    statusGranted: 'マーケティング同意は有効です',
    statusOff: 'マーケティング情報はオフです',
    statusWithdrawn: '同意していないか、同意を撤回しました',
    statusExpired: '以前の同意は現在無効です',
    statusContactBlocked:
      '同意は保存されましたが、この電話番号へのマーケティング配信は引き続きブロックされています',
    statusUnavailable: '現在この設定は変更できません',
    resolveContactBlock: 'メッセージの再開を試す',
    grant: '同意する',
    decline: '同意しない',
    renew: '更新された通知に同意する',
    staleNotice:
      '通知が更新されました。最新版を確認し、同意する場合はもう一度確定してください。',
  },
  hi: {
    continueLogin: 'जारी रखें',
    navigationTitle: 'गोपनीयता सेटिंग',
    pageHeading: 'गोपनीयता सेटिंग',
    sectionTitle: 'मार्केटिंग संदेश',
    sectionDescription:
      'चुनें कि OLULO प्रचार, समाचार और मार्केटिंग संदेश भेजने के लिए आपके डेटा का उपयोग कर सकता है या नहीं।',
    storeSectionTitle: 'स्टोर के साथ डेटा साझा करना',
    statusNotChosen: 'आपने अभी कोई विकल्प नहीं चुना है।',
    statusGranted: 'मार्केटिंग सहमति वर्तमान है',
    statusOff: 'मार्केटिंग संदेश बंद हैं',
    statusWithdrawn: 'सहमति अस्वीकार या वापस ली गई',
    statusExpired: 'आपकी पिछली सहमति अब मान्य नहीं है',
    statusContactBlocked:
      'आपकी सहमति सहेज ली गई है, लेकिन इस फ़ोन नंबर पर मार्केटिंग अभी भी अवरुद्ध है',
    statusUnavailable: 'यह विकल्प अभी बदला नहीं जा सकता',
    resolveContactBlock: 'संदेश फिर चालू करने का प्रयास करें',
    grant: 'सहमति दें',
    decline: 'सहमति न दें',
    renew: 'अपडेट किए गए नोटिस से सहमत हों',
    staleNotice:
      'नोटिस बदल गया है। नया संस्करण पढ़ें और सहमत होने के लिए फिर पुष्टि करें।',
  },
  de: {
    continueLogin: 'Weiter',
    navigationTitle: 'Datenschutzeinstellungen',
    pageHeading: 'Datenschutzeinstellungen',
    sectionTitle: 'Marketingmitteilungen',
    sectionDescription:
      'Lege fest, ob OLULO deine Daten für Angebote, Neuigkeiten und Marketingmitteilungen verwenden darf.',
    storeSectionTitle: 'Datenfreigabe für Stores',
    statusNotChosen: 'Du hast noch keine Auswahl getroffen.',
    statusGranted: 'Marketingeinwilligung ist aktuell',
    statusOff: 'Marketingmitteilungen sind deaktiviert',
    statusWithdrawn: 'Einwilligung abgelehnt oder widerrufen',
    statusExpired: 'Deine frühere Einwilligung ist nicht mehr aktuell',
    statusContactBlocked:
      'Deine Einwilligung ist gespeichert, aber Marketing bleibt für diese Telefonnummer gesperrt',
    statusUnavailable: 'Diese Einstellung kann derzeit nicht geändert werden',
    resolveContactBlock: 'Nachrichten wieder aktivieren',
    grant: 'Einwilligen',
    decline: 'Nicht einwilligen',
    renew: 'Aktualisiertem Hinweis zustimmen',
    staleNotice:
      'Der Hinweis wurde geändert. Lies die neue Version und bestätige erneut, wenn du zustimmen möchtest.',
  },
  it: {
    continueLogin: 'Continua',
    navigationTitle: 'Impostazioni privacy',
    pageHeading: 'Impostazioni privacy',
    sectionTitle: 'Comunicazioni di marketing',
    sectionDescription:
      'Scegli se OLULO può usare i tuoi dati per inviare promozioni, novità e comunicazioni di marketing.',
    storeSectionTitle: 'Condivisione dei dati con i negozi',
    statusNotChosen: 'Non hai ancora scelto una preferenza.',
    statusGranted: 'Consenso marketing attuale',
    statusOff: 'Le comunicazioni di marketing sono disattivate',
    statusWithdrawn: 'Consenso rifiutato o revocato',
    statusExpired: 'Il consenso precedente non è più attuale',
    statusContactBlocked:
      'Il consenso è stato salvato, ma il marketing resta bloccato per questo numero',
    statusUnavailable: 'Questa preferenza non può essere modificata ora',
    resolveContactBlock: 'Prova a riattivare i messaggi',
    grant: 'Acconsenti',
    decline: 'Non acconsentire',
    renew: 'Accetta l’informativa aggiornata',
    staleNotice:
      'L’informativa è cambiata. Leggi la versione aggiornata e conferma di nuovo per accettare.',
  },
  ru: {
    continueLogin: 'Продолжить',
    navigationTitle: 'Настройки конфиденциальности',
    pageHeading: 'Настройки конфиденциальности',
    sectionTitle: 'Маркетинговые сообщения',
    sectionDescription:
      'Выберите, может ли OLULO использовать ваши данные для отправки акций, новостей и маркетинговых сообщений.',
    storeSectionTitle: 'Передача данных заведениям',
    statusNotChosen: 'Вы еще не выбрали настройку.',
    statusGranted: 'Маркетинговое согласие актуально',
    statusOff: 'Маркетинговые сообщения отключены',
    statusWithdrawn: 'Согласие отклонено или отозвано',
    statusExpired: 'Предыдущее согласие больше не действует',
    statusContactBlocked:
      'Согласие сохранено, но маркетинговые сообщения для этого номера по-прежнему заблокированы',
    statusUnavailable: 'Сейчас эту настройку изменить нельзя',
    resolveContactBlock: 'Попробовать восстановить сообщения',
    grant: 'Дать согласие',
    decline: 'Не соглашаться',
    renew: 'Принять обновленное уведомление',
    staleNotice:
      'Уведомление изменилось. Прочитайте новую версию и подтвердите согласие еще раз.',
  },
  ar: {
    continueLogin: 'متابعة',
    navigationTitle: 'إعدادات الخصوصية',
    pageHeading: 'إعدادات الخصوصية',
    sectionTitle: 'رسائل التسويق',
    sectionDescription:
      'اختر ما إذا كان بإمكان OLULO استخدام بياناتك لإرسال العروض والأخبار والرسائل التسويقية.',
    storeSectionTitle: 'مشاركة البيانات مع المتاجر',
    statusNotChosen: 'لم تختر تفضيلاً بعد.',
    statusGranted: 'موافقة التسويق سارية',
    statusOff: 'الرسائل التسويقية متوقفة',
    statusWithdrawn: 'تم رفض الموافقة أو سحبها',
    statusExpired: 'موافقتك السابقة لم تعد سارية',
    statusContactBlocked:
      'تم حفظ موافقتك، لكن الرسائل التسويقية ما زالت محظورة لهذا الرقم',
    statusUnavailable: 'لا يمكن تغيير هذا التفضيل الآن',
    resolveContactBlock: 'محاولة إعادة تفعيل الرسائل',
    grant: 'الموافقة',
    decline: 'عدم الموافقة',
    renew: 'الموافقة على الإشعار المحدّث',
    staleNotice:
      'تم تغيير الإشعار. راجع النسخة المحدّثة وأكّد مرة أخرى إذا أردت الموافقة.',
  },
  vi: {
    continueLogin: 'Tiếp tục',
    navigationTitle: 'Cài đặt quyền riêng tư',
    pageHeading: 'Cài đặt quyền riêng tư',
    sectionTitle: 'Thông tin tiếp thị',
    sectionDescription:
      'Chọn xem OLULO có thể dùng dữ liệu của bạn để gửi ưu đãi, tin tức và thông tin tiếp thị hay không.',
    storeSectionTitle: 'Chia sẻ dữ liệu với cửa hàng',
    statusNotChosen: 'Bạn chưa chọn tùy chọn.',
    statusGranted: 'Đồng ý tiếp thị đang có hiệu lực',
    statusOff: 'Thông tin tiếp thị đang tắt',
    statusWithdrawn: 'Đã từ chối hoặc rút lại đồng ý',
    statusExpired: 'Đồng ý trước đây không còn hiệu lực',
    statusContactBlocked:
      'Sự đồng ý đã được lưu, nhưng tiếp thị vẫn bị chặn đối với số điện thoại này',
    statusUnavailable: 'Hiện không thể thay đổi tùy chọn này',
    resolveContactBlock: 'Thử bật lại tin nhắn',
    grant: 'Đồng ý',
    decline: 'Không đồng ý',
    renew: 'Đồng ý với thông báo mới',
    staleNotice:
      'Thông báo đã thay đổi. Hãy xem bản mới và xác nhận lại nếu bạn muốn đồng ý.',
  },
  th: {
    continueLogin: 'ดำเนินการต่อ',
    navigationTitle: 'การตั้งค่าความเป็นส่วนตัว',
    pageHeading: 'การตั้งค่าความเป็นส่วนตัว',
    sectionTitle: 'การสื่อสารการตลาด',
    sectionDescription:
      'เลือกว่าจะอนุญาตให้ OLULO ใช้ข้อมูลของคุณเพื่อส่งโปรโมชัน ข่าวสาร และข้อมูลการตลาดหรือไม่',
    storeSectionTitle: 'การแชร์ข้อมูลกับร้านค้า',
    statusNotChosen: 'คุณยังไม่ได้เลือกการตั้งค่า',
    statusGranted: 'ความยินยอมด้านการตลาดยังมีผล',
    statusOff: 'ปิดการสื่อสารการตลาดแล้ว',
    statusWithdrawn: 'ปฏิเสธหรือถอนความยินยอมแล้ว',
    statusExpired: 'ความยินยอมเดิมไม่มีผลแล้ว',
    statusContactBlocked:
      'บันทึกความยินยอมแล้ว แต่การตลาดยังถูกบล็อกสำหรับหมายเลขโทรศัพท์นี้',
    statusUnavailable: 'ขณะนี้ไม่สามารถเปลี่ยนการตั้งค่านี้ได้',
    resolveContactBlock: 'ลองเปิดรับข้อความอีกครั้ง',
    grant: 'ให้ความยินยอม',
    decline: 'ไม่ยินยอม',
    renew: 'ยอมรับประกาศฉบับปรับปรุง',
    staleNotice:
      'ประกาศมีการเปลี่ยนแปลง โปรดอ่านฉบับใหม่และยืนยันอีกครั้งหากต้องการยินยอม',
  },
  nl: {
    continueLogin: 'Doorgaan',
    navigationTitle: 'Privacyinstellingen',
    pageHeading: 'Privacyinstellingen',
    sectionTitle: 'Marketingberichten',
    sectionDescription:
      'Kies of OLULO je gegevens mag gebruiken voor aanbiedingen, nieuws en marketingberichten.',
    storeSectionTitle: 'Gegevens delen met Stores',
    statusNotChosen: 'Je hebt nog geen voorkeur gekozen.',
    statusGranted: 'Marketingtoestemming is actueel',
    statusOff: 'Marketingberichten staan uit',
    statusWithdrawn: 'Toestemming geweigerd of ingetrokken',
    statusExpired: 'Je eerdere toestemming is niet meer actueel',
    statusContactBlocked:
      'Je toestemming is opgeslagen, maar marketing blijft geblokkeerd voor dit telefoonnummer',
    statusUnavailable: 'Deze voorkeur kan nu niet worden gewijzigd',
    resolveContactBlock: 'Berichten opnieuw proberen te activeren',
    grant: 'Toestemming geven',
    decline: 'Geen toestemming geven',
    renew: 'Akkoord met de bijgewerkte kennisgeving',
    staleNotice:
      'De kennisgeving is gewijzigd. Lees de nieuwe versie en bevestig opnieuw als je akkoord wilt gaan.',
  },
};

const withdrawalCopyByLocale: Record<Locale, MarketingWithdrawalCopy> = {
  es: {
    withdrawConfirmTitle: '¿Retirar el consentimiento de marketing?',
    withdrawConfirmBody:
      'OLULO dejará de usar tus datos personales para enviarte promociones, novedades y otras comunicaciones de marketing. Puedes volver a otorgar el consentimiento después.',
    withdrawConfirmCancel: 'Conservar consentimiento',
    withdrawConfirmAction: 'Sí, retirar',
  },
  en: {
    withdrawConfirmTitle: 'Withdraw marketing consent?',
    withdrawConfirmBody:
      'OLULO will stop using your personal data to send promotions, news, and other marketing communications. You can grant consent again later.',
    withdrawConfirmCancel: 'Keep consent',
    withdrawConfirmAction: 'Yes, withdraw',
  },
  ko: {
    withdrawConfirmTitle: '마케팅 동의를 철회할까요?',
    withdrawConfirmBody:
      'OLULO는 프로모션, 소식 및 기타 마케팅 정보를 보내기 위해 개인정보를 더 이상 이용하지 않습니다. 나중에 다시 동의할 수 있습니다.',
    withdrawConfirmCancel: '동의 유지',
    withdrawConfirmAction: '철회하기',
  },
  pt: {
    withdrawConfirmTitle: 'Retirar o consentimento de marketing?',
    withdrawConfirmBody:
      'A OLULO deixará de usar seus dados pessoais para enviar promoções, novidades e outras comunicações de marketing. Você poderá consentir novamente depois.',
    withdrawConfirmCancel: 'Manter consentimento',
    withdrawConfirmAction: 'Sim, retirar',
  },
  fr: {
    withdrawConfirmTitle: 'Retirer le consentement marketing ?',
    withdrawConfirmBody:
      'OLULO cessera d’utiliser vos données personnelles pour envoyer des promotions, actualités et autres communications marketing. Vous pourrez consentir de nouveau plus tard.',
    withdrawConfirmCancel: 'Conserver le consentement',
    withdrawConfirmAction: 'Oui, retirer',
  },
  zh: {
    withdrawConfirmTitle: '撤回营销同意？',
    withdrawConfirmBody:
      'OLULO 将停止使用您的个人数据发送优惠、资讯和其他营销信息。您之后可以再次同意。',
    withdrawConfirmCancel: '保留同意',
    withdrawConfirmAction: '确认撤回',
  },
  ja: {
    withdrawConfirmTitle: 'マーケティング同意を撤回しますか？',
    withdrawConfirmBody:
      'OLULOは、プロモーション、ニュース、その他のマーケティング情報を送るための個人データ利用を停止します。後で再度同意できます。',
    withdrawConfirmCancel: '同意を維持',
    withdrawConfirmAction: '撤回する',
  },
  hi: {
    withdrawConfirmTitle: 'मार्केटिंग सहमति वापस लें?',
    withdrawConfirmBody:
      'OLULO प्रचार, समाचार और अन्य मार्केटिंग संदेश भेजने के लिए आपके निजी डेटा का उपयोग बंद कर देगा। आप बाद में फिर सहमति दे सकते हैं।',
    withdrawConfirmCancel: 'सहमति बनाए रखें',
    withdrawConfirmAction: 'हाँ, वापस लें',
  },
  de: {
    withdrawConfirmTitle: 'Marketingeinwilligung widerrufen?',
    withdrawConfirmBody:
      'OLULO verwendet deine personenbezogenen Daten dann nicht mehr für Angebote, Neuigkeiten und andere Marketingmitteilungen. Du kannst später erneut einwilligen.',
    withdrawConfirmCancel: 'Einwilligung behalten',
    withdrawConfirmAction: 'Ja, widerrufen',
  },
  it: {
    withdrawConfirmTitle: 'Revocare il consenso marketing?',
    withdrawConfirmBody:
      'OLULO smetterà di usare i tuoi dati personali per inviare promozioni, novità e altre comunicazioni di marketing. Potrai acconsentire di nuovo in seguito.',
    withdrawConfirmCancel: 'Mantieni consenso',
    withdrawConfirmAction: 'Sì, revoca',
  },
  ru: {
    withdrawConfirmTitle: 'Отозвать согласие на маркетинг?',
    withdrawConfirmBody:
      'OLULO перестанет использовать ваши персональные данные для акций, новостей и других маркетинговых сообщений. Позже вы сможете согласиться снова.',
    withdrawConfirmCancel: 'Сохранить согласие',
    withdrawConfirmAction: 'Да, отозвать',
  },
  ar: {
    withdrawConfirmTitle: 'سحب الموافقة على التسويق؟',
    withdrawConfirmBody:
      'ستتوقف OLULO عن استخدام بياناتك الشخصية لإرسال العروض والأخبار والرسائل التسويقية الأخرى. يمكنك الموافقة مجددًا لاحقًا.',
    withdrawConfirmCancel: 'الاحتفاظ بالموافقة',
    withdrawConfirmAction: 'نعم، اسحبها',
  },
  vi: {
    withdrawConfirmTitle: 'Rút lại đồng ý tiếp thị?',
    withdrawConfirmBody:
      'OLULO sẽ ngừng dùng dữ liệu cá nhân của bạn để gửi ưu đãi, tin tức và các thông tin tiếp thị khác. Bạn có thể đồng ý lại sau.',
    withdrawConfirmCancel: 'Giữ đồng ý',
    withdrawConfirmAction: 'Có, rút lại',
  },
  th: {
    withdrawConfirmTitle: 'ถอนความยินยอมด้านการตลาด?',
    withdrawConfirmBody:
      'OLULO จะหยุดใช้ข้อมูลส่วนบุคคลของคุณเพื่อส่งโปรโมชัน ข่าวสาร และข้อมูลการตลาดอื่น ๆ คุณสามารถให้ความยินยอมใหม่ภายหลังได้',
    withdrawConfirmCancel: 'คงความยินยอม',
    withdrawConfirmAction: 'ใช่ ถอนความยินยอม',
  },
  nl: {
    withdrawConfirmTitle: 'Marketingtoestemming intrekken?',
    withdrawConfirmBody:
      'OLULO gebruikt je persoonsgegevens dan niet meer voor aanbiedingen, nieuws en andere marketingberichten. Je kunt later opnieuw toestemming geven.',
    withdrawConfirmCancel: 'Toestemming behouden',
    withdrawConfirmAction: 'Ja, intrekken',
  },
};

export function getMarketingConsentStrings(
  locale: Locale
): MarketingConsentStrings {
  const common = getPartnerVisibilityStrings(locale);
  return {
    ...copyByLocale[locale],
    ...withdrawalCopyByLocale[locale],
    noticeLink: common.privacyNoticeLink,
    noticeVersion: common.privacyNoticeVersionLabel,
    withdraw: common.withdraw,
    withdrawPrevious: common.withdrawPreviousGrant,
    loading: common.loading,
    loadError: common.loadError,
    retry: common.retry,
    saving: common.saving,
    mutationError: common.mutationError,
    grantSuccess: common.grantSuccess,
    withdrawSuccess: common.withdrawSuccess,
  };
}
